/**
 * Twelve-hour clock or twenty-four.
 *
 * MeMo decided this from the INTERFACE LANGUAGE: English meant 12h, anything
 * else meant 24h. Loraine, in New York, wrote: "my time zone is set to New York
 * but the time is still showing up in what we call military time". Her clock is
 * a habit of the place she lives in, not of the language she reads the app in —
 * and the app was answering a question she had not asked.
 *
 * So it asks the device. A phone set up in the United States formats an hour as
 * "2 PM" whatever language MeMo happens to be displaying, and that is the
 * answer that matches the person holding it.
 *
 * And now it can also be told. Silvia asked for the choice to live in Settings,
 * and she is right that guessing well is not the same as being sure: somebody
 * with an English phone who wants 24 hours, or an Italian who grew up with
 * AM/PM, had no way to say so. Three states:
 *
 *   'auto'  ask the device, then fall back to the language  (the default)
 *   '12'    always AM/PM
 *   '24'    always 0-23
 *
 * It lives in localStorage rather than in the account, like the other
 * display-only preferences (the accessibility ones do the same). It is about
 * this screen in this hand, and it costs no migration.
 */

const KEY = 'memo_clock';

// Read once and remember: uses12h() is called several times per render, on
// every card. Settings updates this through setClockPreference below.
let cached = null;

export function clockPreference() {
  if (cached === null) {
    try { cached = localStorage.getItem(KEY) || 'auto'; } catch (_) { cached = 'auto'; }
  }
  return cached;
}

export function setClockPreference(v) {
  cached = (v === '12' || v === '24') ? v : 'auto';
  try {
    if (cached === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, cached);
  } catch (_) { /* private mode: it holds for this session and no longer */ }
}

export function uses12h(appLang) {
  const pref = clockPreference();
  if (pref === '12') return true;
  if (pref === '24') return false;
  try {
    const dev = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages || [])[0])) || null;
    if (dev) {
      const h12 = new Intl.DateTimeFormat(dev, { hour: 'numeric' }).resolvedOptions().hour12;
      if (typeof h12 === 'boolean') return h12;
    }
  } catch (_) {
    // An environment that cannot resolve its own locale falls through.
  }
  return String(appLang || '').startsWith('en');
}

export default uses12h;

/**
 * Monday or Sunday.
 *
 * MeMo has always started the week on Monday, which is right for Italy and
 * wrong for the United States — and MeMo now has testers in both. Silvia: "gli
 * anglosassoni iniziano la settimana da domenica, noi da lunedì: diamo la
 * possibilità all'utente di scegliere".
 *
 * A display preference like the clock, kept in the same place, and it must be
 * ONE answer: the week list, the week grid, the month and the year all draw
 * their columns from this, and two of them disagreeing would be worse than
 * either choice.
 *
 * 1 = Monday, 0 = Sunday — the numbers date-fns uses, so the value can go
 * straight into weekStartsOn with nothing to translate on the way.
 */
const WEEK_KEY = 'memo_week_start';
let cachedWeek = null;

/** 'auto' | 'mon' | 'sun' — what was chosen, if anything. */
export function weekPreference() {
  try { return localStorage.getItem(WEEK_KEY) || 'auto'; } catch (_) { return 'auto'; }
}

/**
 * The day the week begins on, asked of the phone when nobody has said.
 *
 * This used to be hard-coded to Monday, which meant an American saw a Monday
 * week until they went and found the setting — the same shape of fault as
 * Loraine's "military time", where MeMo decided something about her country
 * without asking her country. The phone knows: Intl reports the first day of
 * the week for its locale, 1 for Monday through 7 for Sunday.
 */
export function weekStartsOn() {
  if (cachedWeek !== null) return cachedWeek;
  const pref = weekPreference();
  if (pref === 'mon') { cachedWeek = 1; return cachedWeek; }
  if (pref === 'sun') { cachedWeek = 0; return cachedWeek; }
  cachedWeek = 1;
  try {
    const loc = (typeof navigator !== 'undefined' && navigator.language) || '';
    // Intl counts 1 = Monday through 7 = Sunday; date-fns counts 0 = Sunday
    // through 6 = Saturday. The modulo does the whole conversion: 7 becomes 0.
    // Saturday (6) is right for much of the Arabic-speaking world and arrives
    // correctly without a special case.
    // weekInfo is not on every browser yet; where it is missing, Monday stands.
    const first = loc ? new Intl.Locale(loc).weekInfo?.firstDay : undefined;
    if (typeof first === 'number') cachedWeek = first % 7;
  } catch (_) { /* Monday stands */ }
  return cachedWeek;
}

export function setWeekPreference(v) {
  const pref = (v === 'mon' || v === 'sun') ? v : 'auto';
  cachedWeek = null;                       // worked out again on the next read
  try {
    if (pref === 'auto') localStorage.removeItem(WEEK_KEY);
    else localStorage.setItem(WEEK_KEY, pref);
  } catch (_) { /* private mode: it holds for this session and no longer */ }
}
